import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ShepherdService } from 'angular-shepherd';
import { finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { Store } from '@ngrx/store';
import { get } from 'lodash';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { BaseModalComponent } from 'common/src/modules/rnpl-common/components';
import { ProductTypes } from '../../../products/product-types';
import { CreditNoteApiService } from 'projects/workspace/src/app/credit-note/services/credit-note-api.service';
import { CreditNotePositionModel } from 'projects/workspace/src/app/credit-note/models/credit-note-position.model';
import { CustomSearchFn } from '../../../rnpl-common/helpers';
import { PauseModalsHintsComponent } from '../../modals-subscription/pause-modals-hints/pause-modals-hints.component';
import { DocumentTypesUppercaseEnum } from '../../modals-common/link-document-modal/enums/ducument-types.enum';
import { RecalculatePositionApiService } from '../../../../services/recalculate-position-api.service';
import { CreditNotePurposeEnum } from 'projects/workspace/src/app/credit-note/enums';
import { PositionAccountingSettings } from 'projects/workspace/src/app/outgoing-invoice/models';
import { PositionAccountingSettingsComponent } from 'projects/workspace/src/app/shared/components';
import { PurposeTypeEnum } from 'projects/workspace/src/app/purchase-order/enums';
import { ProductUnitModel } from 'projects/workspace/src/app/shared/models';
import { ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { PaymentDirectionEnum } from 'projects/workspace/src/app/payment/models/payment.model';
import { CreditNoteModel } from 'projects/workspace/src/app/credit-note/models';
import { ReportingPeriodsPreferencesModel } from 'projects/workspace/src/app/reporting-periods/models';
import { selectReportingSettings } from 'projects/workspace/src/app/store/selectors/shared.selectors';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';

@Component({
  selector: 'rnpl-credit-note-position-modal',
  templateUrl: './credit-note-position-modal.component.html',
  styleUrls: ['./credit-note-position-modal.component.scss']
})
export class CreditNotePositionModalComponent extends BaseModalComponent implements OnInit {

  public companyProfile: CompanyProfile;
  public selectedProductInvalid: boolean = false;
  public selectedProduct: Partial<CreditNotePositionModel> = null;
  public productsList: CreditNotePositionModel[] = [];
  public selectedProductType: ProductTypes = ProductTypes.GOODS;
  public accountingSettings: PositionAccountingSettings;
  public reportingPeriodPreferences: ReportingPeriodsPreferencesModel;

  public productNameInvalid: boolean = false;
  public positionName: string = null;
  public purposeTypeEnum = PurposeTypeEnum;
  public productTypes: typeof ProductTypes = ProductTypes;
  public allowFractionalValues: boolean = true;
  public form: FormGroup;

  public customSearchFn = CustomSearchFn;

  readonly showDropdownSpin$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly addPositionRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @ViewChild('hint', { static: false }) hint: PauseModalsHintsComponent;
  @ViewChild('positionAccountingSettings', { static: false }) positionAccountingSettingsRef: PositionAccountingSettingsComponent;


  constructor(
    public store: Store<AppState>,
    public toasterService: ToasterService,
    public creditNoteApiService: CreditNoteApiService,
    public productUnitApiService: ProductUnitApiService,
    public dialogRef: MatDialogRef<CreditNotePositionModalComponent>,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private recalculatePositionApiService: RecalculatePositionApiService,
    @Inject(MAT_DIALOG_DATA) public data: {
      creditNote: CreditNoteModel,
      creditNoteId: number,
      isIncomingCorrection: boolean,
      vatBlocked: boolean,
      purpose: CreditNotePurposeEnum,
      disabledAccountingSettings: boolean,
    },
    private shepherdService: ShepherdService
  ) {
    super(toasterService);

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => this.companyProfile = companyProfile);

    this.store.select(selectReportingSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((preferences: ReportingPeriodsPreferencesModel) => {
        this.reportingPeriodPreferences = preferences;
      });
  }

  ngOnInit() {
    this.initForm(this.selectedProduct);
    if (this.shepherdService.isActive) {
      this.shepherdService.cancel();
      this.shepherdService.complete();
    }

    this.getPositions();
  }

  private getPositions(): void {
    this.creditNoteApiService.getAvailableCreditNotePositions(this.data.creditNoteId)
      .pipe(
        finalize(() => {
          this.isLoading$.next(false);
          this.showDropdownSpin$.next(false);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(positionsByProductType => {
        for (const key in positionsByProductType) {
          if (positionsByProductType.hasOwnProperty(key)) {
            this.productsList.push(...positionsByProductType[key]);
          }
        }
        this.productsList = [...this.productsList];
      });
  }

  public addPosition(): void {
    if (this.addPositionRequest$.getValue()) { return; }

    if (!this.selectedProduct) {
      this.selectedProductInvalid = true;
      return;
    }

    // if (this.isArbitraryProductType) {
    //   if (!this.description.valid) {
    //     this.productNameInvalid = true;
    //     return;
    //   }
    // }

    const position: CreditNotePositionModel = {
      ...this.form.getRawValue(),
      positionId: this.selectedProduct.positionId,
      productTypeForGrouping: this.selectedProductType,
      generalProductId: this.isArbitraryProductType
        ? +this.selectedProduct.generalProductId
        : null,
      productId: this.isArbitraryProductType
        ? +this.selectedProduct.productId
        : null,
      runpleId: this.isArbitraryProductType
        ? this.selectedProduct.runpleId
        : null,
      productType: this.isArbitraryProductType
        ? ProductTypes.GOODS
        : this.selectedProductType,
      vat: this.isArbitraryProductType ? 0 : this.vat.value,
      sourcePositionId: this.selectedProduct.positionId,
      productDescription: !this.isProductTypeServices &&  !this.isArbitraryProductType
        ? this.productDescription.value
        : null,
      providedServices: this.isProductTypeServices || this.isArbitraryProductType
        ? this.providedServices.value
        : null,
      grossUnitPrice: this.isArbitraryProductType
        ? '0.00'
        : this.selectedProduct.grossUnitPrice,
      netUnitPrice: this.isArbitraryProductType
        ? '0.00'
        : this.selectedProduct.netUnitPrice,
      accountingSettings: {
        ...this.accountingSettings,
        ...(this.positionAccountingSettingsRef && this.positionAccountingSettingsRef.getFormValue())
      }
    };

    this.creditNoteApiService.createPositionByPredefinedForm(this.data.creditNoteId, position)
      // .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.dialogRef.close(response);
        this.getCreditNoteById();
      });
  }

  public getCreditNoteById() {
    this.creditNoteApiService.getCreditNoteById(this.data .creditNoteId).subscribe();
  }

  // public displayUserDefinedPosition(): void {
  //   this.changeProductType();
  //   this.selectedProductType = ProductTypes.ARBITRARY;
  //   this.selectedProduct = { description: this.translateService.instant('POSITIONS.USER_DEFINED_POSITION') };
  //   this.initForm();
  //   this.description.setValidators(Validators.required);
  //   this.quantity.setValue(1);
  //   if (this.data.vatBlocked) {
  //     this.vat.setValue(0);
  //     this.vat.disable({onlySelf: true, emitEvent: false});
  //   } else {
  //     this.vat.setValue(20);
  //   }
  // }

  public changeProductType(): void {
    this.selectedProduct = null;
    this.positionName = null;
    this.productNameInvalid = false;
    this.selectedProductInvalid = false;
  }

  public initForm(selectProduct: Partial<CreditNotePositionModel> = {} as Partial<CreditNotePositionModel>): void {
    this.form = this.fb.group({
      sourceAmountGross: [],
      netTotalPrice: [null, Validators.required],
      productDescription: [get(selectProduct, 'productDescription', '')],
      providedServices: [get(selectProduct, 'providedServices', '')],
      description: [get(selectProduct, 'description')],
      unitType: [get(selectProduct, 'unitType') || 'pcs'],
      bundleType: [get(selectProduct, 'bundleType')],
      netUnitPrice: [],
      sourceAmountNet: [],
      vat: [get(selectProduct, 'vat')],
      quantity: [0],
      sourceQuantity: [],
      remark: [],
      addRemark: [false],
      vatTotal: [],
      grossTotalPrice: [null, Validators.required],
      productDescriptionUpdated: [false]
    });
  }

  selectPositionChanges(selectedProduct: Partial<CreditNotePositionModel>): void {
    this.selectedProductType = selectedProduct.productType;
    this.accountingSettings = null;
    if (this.positionAccountingSettingsRef) {
      this.positionAccountingSettingsRef.resetForm();
    }
    this.accountingSettings = selectedProduct.accountingSettings;

    this.initForm(selectedProduct);
    this.description.clearValidators();
    if (this.data.vatBlocked) {
      this.vat.setValue(0);
      this.vat.disable({onlySelf: true, emitEvent: false});
      this.vatTotal.disable({onlySelf: true, emitEvent: false});
    }
    if (this.isPrepaymentProductType) {
      this.quantity.patchValue(1);
      this.vat.disable({onlySelf: true, emitEvent: false});
      this.netTotalPrice.patchValue(selectedProduct.netTotalPrice, {onlySelf: true, emitEvent: false});
      this.grossTotalPrice.patchValue(selectedProduct.grossTotalPrice, {onlySelf: true, emitEvent: false});
      this.netTotalPrice.disable({onlySelf: true, emitEvent: false});
      this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
    }
    this.getUnitTypes();
  }

  public getUnitTypes(): void {
    this.productUnitApiService.getUnits$(this.selectedProductType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        const allowFractionalValues = unitsList.find(u => u.name === this.unitType.value) &&
          unitsList.find(u => u.name === this.unitType.value).allowFractionalValues;
        const isFractionalQuantity: boolean = !!(this.selectedProduct.quantity % 1);
        this.allowFractionalValues = allowFractionalValues || isFractionalQuantity;
      });
  }

  public updatePosition(value, fieldName): void {
    this.addPositionRequest$.next(true);

    this.recalculatePositionApiService.recalculatePosition(DocumentTypesUppercaseEnum.CRN, {
      protoPosition: {
        ...this.form.getRawValue(),
        productId: this.selectedProduct.productId,
        vat: this.isArbitraryProductType ? 0 : this.vat.value,
        sourcePositionId: this.selectedProduct.positionId,
        productTypeForGrouping: this.selectedProductType,
        productType: this.isArbitraryProductType
          ? ProductTypes.GOODS
          : this.selectedProductType,
        productDescription: (!this.isProductTypeServices && !this.isArbitraryProductType)
          ? this.productDescription.value
          : null,
        providedServices: this.isProductTypeServices || this.isArbitraryProductType
          ? this.providedServices.value
          : null,
        grossUnitPrice: this.isArbitraryProductType
          ? '0.00'
          : this.selectedProduct.grossUnitPrice,
        netUnitPrice: this.isArbitraryProductType
          ? '0.00'
          : this.selectedProduct.netUnitPrice,
        accountingSettings: {
          ...this.accountingSettings,
          ...(this.positionAccountingSettingsRef && this.positionAccountingSettingsRef.getFormValue())
        }
      },
      fieldUpdateRequest: {
        fieldName: fieldName,
        fieldValue: value
      },
      type: this.selectedProductType,
      crnPurpose: this.data.purpose,
      documentId: this.data.creditNoteId,
    })
      // .pipe(takeUntil(this.destroy$))
      .subscribe(product => {
        this.addPositionRequest$.next(false);
        this.form.patchValue(product);
      });
  }

  public updateDescription(description: string): void {
    this.selectedProduct = {
      ...this.selectedProduct,
      productDescription: description,
      providedServices: description,
    };
  }

  public updateDescriptionByProductType(): void {
    if (this.isProductTypeServices || this.isArbitraryProductType) {
      this.updatePosition(this.providedServices.value, 'providedServices');
    } else {
      this.updatePosition(this.productDescription.value, 'productDescription');
    }
  }

  get isProductTypeGoods(): boolean {
    return this.selectedProductType === ProductTypes.GOODS;
  }

  get isProductTypeServices(): boolean {
    return this.selectedProductType === ProductTypes.SERVICES;
  }

  get isProductTypeDigital(): boolean {
    return this.selectedProductType === ProductTypes.DIGITAL;
  }

  get isPrepaymentProductType(): boolean {
    return this.selectedProductType === ProductTypes.PREPAYMENT;
  }

  public isOinOrIinCorrection(): boolean {
    return this.data.purpose === CreditNotePurposeEnum.OIN_CORRECTION ||
           this.data.purpose === CreditNotePurposeEnum.IIN_CORRECTION;
  }

  get isIinCorrection(): boolean {
    return this.data.purpose === CreditNotePurposeEnum.IIN_CORRECTION;
  }
  get isOinCorrection(): boolean {
    return this.data.purpose === CreditNotePurposeEnum.OIN_CORRECTION;
  }
  get isGeneralProductType(): boolean {
    return this.selectedProduct.general;
  }
  get isArbitraryProductType(): boolean {
    return this.selectedProductType === ProductTypes.ARBITRARY;
  }

  get isDirectionOutgoing(): boolean {
    return this.data.creditNote.properties.direction === PaymentDirectionEnum.OUTGOING;
  }

  get isLogistics(): boolean {
    return this.selectedProduct.logistics;
  }

  get isPurposeInternal(): boolean {
    return this.selectedProduct.purpose === PurposeTypeEnum.INTERNAL_USAGE;
  }

  get priceOptionNet(): boolean {
    return this.data.creditNote.preferences.priceOption === PriceOptionEnum.NET
      || (this.data.creditNote.properties.smallBusiness && this.data.creditNote.properties.direction === PaymentDirectionEnum.OUTGOING);
  }
  get priceOptionGross(): boolean {
    return this.data.creditNote.preferences.priceOption === PriceOptionEnum.GROSS
      && !(this.data.creditNote.properties.smallBusiness && this.data.creditNote.properties.direction === PaymentDirectionEnum.OUTGOING);
  }

  get addRemark(): FormControl { return this.form.get('addRemark') as FormControl; }
  get netTotalPrice(): FormControl { return this.form.get('netTotalPrice') as FormControl; }
  get netUnitPrice(): FormControl { return this.form.get('netUnitPrice') as FormControl; }
  get productDescription(): FormControl { return this.form.get('productDescription') as FormControl; }
  get providedServices(): FormControl { return this.form.get('providedServices') as FormControl; }
  get description(): FormControl { return this.form.get('description') as FormControl; }
  get quantity(): FormControl { return this.form.get('quantity') as FormControl; }
  get remark(): FormControl { return this.form.get('remark') as FormControl; }
  get vat(): FormControl { return this.form.get('vat') as FormControl; }
  get sourceAmountNet(): FormControl { return this.form.get('sourceAmountNet') as FormControl; }
  get sourceAmountGross(): FormControl { return this.form.get('sourceAmountGross') as FormControl; }
  get vatTotal(): FormControl { return this.form.get('vatTotal') as FormControl; }
  get grossTotalPrice(): FormControl { return this.form.get('grossTotalPrice') as FormControl; }
  get unitType(): FormControl { return this.form.get('unitType') as FormControl; }

}
